import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type PartnerInvitePreview } from '@/api/routes/public/partnerInvites.api';
import { formatInviteDate } from '@/domain/invite/utils';
import { useTranslate } from '@/state/i18n';
import { controlTokens } from '@/theme/themePrimitives';

interface Props {
  invite: PartnerInvitePreview;
}

/** Whose account, with which access, for which e-mail and until when. */
const InviteSummary = ({ invite }: Props) => {
  const t = useTranslate('domain.invite.components.InviteSummary');
  const isClaim = invite.role === 'owner';

  return (
    <Stack alignItems="center" textAlign="center" gap={1.5}>
      <Avatar src={invite.partner.logoUrl ?? undefined} variant="rounded" sx={sx.logo}>
        {invite.partner.displayName.slice(0, 2).toUpperCase()}
      </Avatar>
      <Typography component="h1" variant="h4">
        {t(isClaim ? 'claimTitle' : 'memberTitle', { partner: invite.partner.displayName })}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t(`role.${invite.role}`)}
      </Typography>
      <Stack gap={0.25}>
        <Typography variant="body2">
          {t('invitedEmail')} <strong>{invite.email}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('expires', { date: formatInviteDate(invite.expiresAt) })}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default InviteSummary;

const sx = {
  logo: {
    width: 72,
    height: 72,
    borderRadius: '18px',
    fontSize: 22,
    fontWeight: 800,
    bgcolor: controlTokens.primary,
    color: 'common.white',
  },
};
